import {
  Alert,
  Button,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  CreateSupportRequest,
  SupportRequestPriority,
  SupportRequestType,
} from '../../apiClients/SupportClient';
import useViewportMode from '../../hooks/useViewportMode';
import { ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from '../responsive';

export interface CreateSupportRequestDialogProps {
  open: boolean;
  initialType: SupportRequestType;
  routeId: string;
  busy?: boolean;
  error?: string;
  onClose: () => void;
  onSubmit: (request: CreateSupportRequest) => void | Promise<void>;
}

const emptyRequest = (type: SupportRequestType): CreateSupportRequest => ({
  Type: type,
  Title: '',
  Description: '',
  Priority: 'Normal',
});

const CreateSupportRequestDialog = ({
  open,
  initialType,
  routeId,
  busy = false,
  error,
  onClose,
  onSubmit,
}: CreateSupportRequestDialogProps) => {
  const [request, setRequest] = useState<CreateSupportRequest>(() => emptyRequest(initialType));
  const { mode } = useViewportMode();

  useEffect(() => {
    if (open) setRequest(emptyRequest(initialType));
  }, [initialType, open]);

  const issue = request.Type === 'Issue';
  const valid = Boolean(request.Title.trim()
    && request.Description.trim()
    && (!issue || request.ActualBehavior?.trim()));

  return <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
    <ResponsiveDialogHeader title="Start a new request" onClose={busy ? undefined : onClose} viewportOverride={mode} />
    <DialogContent dividers sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', px: mode === 'phone' ? 2 : 3 }}>
      <Stack spacing={2.25} sx={{ pt: 0.5 }}>
        <Alert severity="info">
          Include the outcome you need and enough context for us to reproduce or evaluate it. Help attaches the current product area, not page contents or private review information.
        </Alert>
        {error && <Alert severity="error">{error}</Alert>}
        <ToggleButtonGroup
          fullWidth
          exclusive
          value={request.Type}
          onChange={(_, value: SupportRequestType) => value && setRequest({ ...request, Type: value })}>
          <ToggleButton value="Issue">Report an issue</ToggleButton>
          <ToggleButton value="FeatureRequest">Request a feature</ToggleButton>
        </ToggleButtonGroup>
        <TextField required label="Title" value={request.Title} onChange={event => setRequest({ ...request, Title: event.target.value })} inputProps={{ maxLength: 160 }} />
        <TextField required multiline minRows={4} label={issue ? 'What happened?' : 'What should Synergist Pro help you accomplish?'} value={request.Description} onChange={event => setRequest({ ...request, Description: event.target.value })} />
        <FormControl><InputLabel>Priority</InputLabel><Select label="Priority" value={request.Priority} onChange={event => setRequest({ ...request, Priority: event.target.value as SupportRequestPriority })}>
          {['Low', 'Normal', 'High', 'Urgent'].map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
        </Select></FormControl>
        {issue ? <>
          <TextField multiline minRows={2} label="Steps to reproduce" value={request.ReproductionSteps || ''} onChange={event => setRequest({ ...request, ReproductionSteps: event.target.value })} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><TextField fullWidth multiline minRows={2} label="Expected behavior" value={request.ExpectedBehavior || ''} onChange={event => setRequest({ ...request, ExpectedBehavior: event.target.value })} /></Grid>
            <Grid item xs={12} md={6}><TextField required fullWidth multiline minRows={2} label="Actual behavior" value={request.ActualBehavior || ''} onChange={event => setRequest({ ...request, ActualBehavior: event.target.value })} /></Grid>
          </Grid>
        </> : <TextField multiline minRows={2} label="Business impact" value={request.BusinessImpact || ''} onChange={event => setRequest({ ...request, BusinessImpact: event.target.value })} />}
      </Stack>
    </DialogContent>
    <StickyDialogActions viewportOverride={mode}>
      <Button disabled={busy} onClick={onClose}>Cancel</Button>
      <Button variant="contained" disabled={busy || !valid} onClick={() => onSubmit({ ...request, RouteId: routeId })}>{busy ? 'Submitting…' : 'Submit request'}</Button>
    </StickyDialogActions>
  </ResponsiveDialog>;
};

export default CreateSupportRequestDialog;
